import { lazy } from 'react';
import { Users, Truck, Settings, Palette, Wrench, CarFront, CalendarCheck, ShieldCheck, CalendarClock, Boxes, Receipt, BarChart3 } from 'lucide-react';

// Static registry of installed frontend modules. Each entry describes
// everything the app shell needs to render navigation/breadcrumbs/routing
// for a module without importing its pages directly (they're lazy-loaded
// for code-splitting).
//
// Shape of a module descriptor:
//   key                 - matches a value that can appear in enabledModules
//   icon                - a lucide-react icon component
//   navLabelKey         - [namespace, key] tuple resolved via t(namespace, key)
//   breadcrumbKey       - [namespace, key] tuple used for the module's own breadcrumb
//   path                - base client-side route path for this module
//   requiredPermission  - permission key required to view the module's list page
//   component           - lazy-loaded root component for the module (handles
//                          its own list/detail sub-routing internally)
//
const CustomersModule = lazy(() => import('./customers/CustomersModule.jsx'));
const VehiclesModule = lazy(() => import('./vehicles/VehiclesModule.jsx'));
const AdministrationModule = lazy(() => import('./administration/AdministrationModule.jsx'));
const BrandingModule = lazy(() => import('./branding/BrandingModule.jsx'));
const WorkOrdersModule = lazy(() => import('./workorders/WorkOrdersModule.jsx'));
const ReplacementVehiclesModule = lazy(() => import('./replacementvehicles/ReplacementVehiclesModule.jsx'));
const ReservationsModule = lazy(() => import('./replacementvehicles/ReservationsModule.jsx'));
const RulesModule = lazy(() => import('./rules/RulesModule.jsx'));
const WorkshopPlanningModule = lazy(() => import('./workshopplanning/WorkshopPlanningModule.jsx'));
const InventoryModule = lazy(() => import('./inventory/InventoryModule.jsx'));
const InvoicingModule = lazy(() => import('./invoicing/InvoicingModule.jsx'));
const ReportingModule = lazy(() => import('./reporting/ReportingModule.jsx'));

export const moduleRegistry = [
  {
    key: 'customers',
    icon: Users,
    navLabelKey: ['common', 'nav.customers'],
    breadcrumbKey: ['customers', 'breadcrumb'],
    path: '/customers',
    requiredPermission: 'customer.read',
    component: CustomersModule,
  },
  {
    key: 'vehicles',
    icon: Truck,
    navLabelKey: ['common', 'nav.vehicles'],
    breadcrumbKey: ['vehicles', 'breadcrumb'],
    path: '/vehicles',
    requiredPermission: 'vehicle.read',
    component: VehiclesModule,
  },
  {
    key: 'administration',
    icon: Settings,
    navLabelKey: ['common', 'nav.administration'],
    breadcrumbKey: ['administration', 'breadcrumb'],
    path: '/administration',
    requiredPermission: 'user.read',
    component: AdministrationModule,
  },
  {
    key: 'branding',
    icon: Palette,
    navLabelKey: ['common', 'nav.branding'],
    breadcrumbKey: ['branding', 'breadcrumb'],
    path: '/branding',
    requiredPermission: 'branding.manage',
    component: BrandingModule,
  },
  {
    key: 'workorders',
    icon: Wrench,
    navLabelKey: ['common', 'nav.workorders'],
    breadcrumbKey: ['workorders', 'breadcrumb'],
    path: '/work-orders',
    requiredPermission: 'workorder.read',
    component: WorkOrdersModule,
  },
  // Two nav entries sharing one `key` -- both gated on the same
  // replacementvehicles tenant_modules toggle, but independently gated by
  // permission: a workshop_manager (replacementvehicle.read + reservation.*)
  // sees "Reservations" but not "Replacement Vehicles"; a fleet_manager
  // sees both.
  {
    key: 'replacementvehicles',
    icon: CarFront,
    navLabelKey: ['common', 'nav.replacementvehicles'],
    breadcrumbKey: ['replacementvehicles', 'breadcrumb'],
    path: '/replacement-vehicles',
    requiredPermission: 'replacementvehicle.read',
    component: ReplacementVehiclesModule,
  },
  {
    key: 'replacementvehicles',
    icon: CalendarCheck,
    navLabelKey: ['common', 'nav.reservations'],
    breadcrumbKey: ['reservations', 'breadcrumb'],
    path: '/reservations',
    requiredPermission: 'reservation.read',
    component: ReservationsModule,
  },
  {
    key: 'rules',
    icon: ShieldCheck,
    navLabelKey: ['common', 'nav.rules'],
    breadcrumbKey: ['rules', 'breadcrumb'],
    path: '/rules',
    requiredPermission: 'rule.read',
    component: RulesModule,
  },
  {
    key: 'workshopplanning',
    icon: CalendarClock,
    navLabelKey: ['common', 'nav.workshopplanning'],
    breadcrumbKey: ['workshopplanning', 'breadcrumb'],
    path: '/workshop-planning',
    requiredPermission: 'workshopresource.read',
    component: WorkshopPlanningModule,
  },
  {
    key: 'inventory',
    icon: Boxes,
    navLabelKey: ['common', 'nav.inventory'],
    breadcrumbKey: ['inventory', 'breadcrumb'],
    path: '/inventory',
    requiredPermission: 'part.read',
    component: InventoryModule,
  },
  {
    key: 'invoicing',
    icon: Receipt,
    navLabelKey: ['common', 'nav.invoicing'],
    breadcrumbKey: ['invoicing', 'breadcrumb'],
    path: '/invoices',
    requiredPermission: 'invoice.read',
    component: InvoicingModule,
  },
  {
    key: 'reporting',
    icon: BarChart3,
    navLabelKey: ['common', 'nav.reporting'],
    breadcrumbKey: ['reporting', 'breadcrumb'],
    path: '/reports',
    requiredPermission: 'report.read',
    component: ReportingModule,
  },
];
